import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, BadgeCheck, ClipboardList, Copy, CreditCard, ExternalLink, Globe, Info, KeyRound, Loader2,
  Lock, LogIn, Mail, Monitor, Receipt, ScanLine, Search, Server, Shield, UserCog, Wrench, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import PageBackgroundsEditor from '../components/PageBackgroundsEditor';
import LanSyncKeyPanel from '../components/LanSyncKeyPanel';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { istInputToBackend, toIstInputValue } from '../utils/datetime';
import { DEFAULT_PUBLIC_SEARCH_FIELDS } from '../utils/formSchemaFields';
import { ROLE_UI_GROUPS, ROLE_UI_ROLES, roleConfig } from '../utils/roleUi';
import { IMAGE_LIMITS, resizeImage } from '../utils/imageResize';
import { rememberEvents, useEventRoute } from '../utils/eventRef';

// Settings saved through their own endpoints rather than the full event update.
const SEPARATE_KEYS = ['teamPortal', 'roleUi', 'customDomain', 'slug'];

const isPaid = (draft) => ['paid', 'PAID'].includes(draft.eventType);

// Every event-level setting, grouped the way organisers think about them. Fields with `type` are rendered
// generically; `custom` names a block below. `on` marks toggles that are on unless explicitly false.
const SECTIONS = [
  {
    id: 'general', title: 'General', icon: Info,
    description: 'Name, dates, venue and pricing basics.',
    fields: [
      { key: 'name', label: 'Event name', type: 'text', required: true },
      { custom: 'slug', label: 'Link name' },
      { key: 'eventType', label: 'Event type', type: 'select', options: [['free', 'Free'], ['paid', 'Paid'], ['limited', 'Limited capacity']], lower: true },
      { key: 'capacity', label: 'Capacity', type: 'number', min: 0 },
      { key: 'location', label: 'Location', type: 'text' },
      { key: 'eventDate', label: 'Starts', type: 'localDateTime' },
      { key: 'eventEndDate', label: 'Ends', type: 'localDateTime' },
      { custom: 'price', label: 'Price', showIf: isPaid },
      { key: 'description', label: 'Description', type: 'textarea', wide: true },
      { key: 'metaData', label: 'Event metadata', help: 'Shown below the event name on the registration page.', type: 'textarea', wide: true },
    ],
  },
  {
    id: 'registration', title: 'Registration', icon: ClipboardList,
    description: 'Open or close online and on-spot registration, limits and messages.',
    fields: [
      { key: 'registrationsClosed', label: 'Close online registrations', type: 'toggle' },
      { key: 'inviteOnly', label: 'Accept registrations only via invite links', help: 'When on, online registration works only through an invite link. On-spot desks are not affected.', type: 'toggle' },
      { key: 'registrationClosedMessage', label: 'Message when online registration is closed or full', type: 'textarea', wide: true },
      { key: 'showFormLogo', label: 'Show logo on the registration form', help: 'Logo banner: 1600 × 400 px (4:1), PNG/WebP under 500 KB.', type: 'toggle', on: true },
      { key: 'showPoweredBy', label: 'Show “Powered by” on the registration form', help: 'Shown at the bottom of the regular form and the premium booking pages.', type: 'toggle', on: true },
      { custom: 'poweredBy', label: 'Powered by logos' },
      { key: 'onSpotAccess', label: 'On-spot registration access', type: 'select', options: [['LOGIN', 'Logged-in staff only'], ['PUBLIC', 'Anyone (logged in or not)'], ['DISABLED', 'Disabled']], fallback: 'LOGIN' },
      { key: 'onSpotRegistrationsClosed', label: 'Close on-spot registrations', type: 'toggle' },
      { key: 'onSpotCapacity', label: 'On-spot capacity', help: 'Blank or 0 means unlimited.', type: 'number', min: 0, zeroClears: true },
      { key: 'onSpotClosedMessage', label: 'Message when on-spot is closed or full', type: 'textarea', wide: true },
      { custom: 'onSpotForm', label: 'On-spot form' },
    ],
  },
  {
    id: 'payments', title: 'Payments & coupons', icon: CreditCard,
    description: 'Approval rules for paid registrations and discount codes.',
    fields: [
      { key: 'autoApproveOnline', label: 'Auto-approve online registrations that submit payment details', type: 'toggle', showIf: isPaid },
      { key: 'autoApproveOnSpot', label: 'Auto-approve on-spot registrations that submit payment details', type: 'toggle', showIf: isPaid },
      { key: 'enableCouponCode', label: 'Let registrants apply coupon codes', type: 'toggle' },
      { custom: 'paymentLinks', label: 'Payment gateway & coupons' },
    ],
  },
  {
    id: 'invoicing', title: 'Invoices (GST)', icon: Receipt,
    description: 'Send a GST tax invoice with each payment instead of the plain receipt.',
    fields: [{ custom: 'invoice', label: 'GST invoice' }],
  },
  {
    id: 'communication', title: 'Notifications', icon: Mail,
    description: 'How attendees hear from you.',
    fields: [
      { key: 'sendEmail', label: 'Email notifications', type: 'toggle', on: true },
      { key: 'sendWhatsapp', label: 'WhatsApp notifications', type: 'toggle', on: true },
      { key: 'showConfirmationEmailButton', label: 'Show "Send Confirmation Email" on registrations', type: 'toggle', on: true },
    ],
  },
  {
    id: 'checkin', title: 'Check-in & scanning', icon: ScanLine,
    description: 'What the scan page shows and how attendees are identified.',
    fields: [
      { key: 'scanManualSearchEnabled', label: 'Show "Search attendee" box on the scan page', type: 'toggle', on: true },
      { key: 'scanQrInputEnabled', label: 'Show QR code / registration ID scanner', type: 'toggle', on: true },
      { key: 'scanShowStats', label: 'Show scan statistics on the scan page', type: 'toggle', on: true },
      { key: 'scanShowLogo', label: 'Show event logo on the scan page', type: 'toggle' },
      { custom: 'scanLogo', label: 'Scan page logo', showIf: (d) => !!d.scanShowLogo },
      { custom: 'publicSearchFields', label: 'Public search fields' },
      { key: 'faceIdEnabled', label: 'Face recognition', type: 'toggle' },
      { key: 'rfIdEnabled', label: 'RFID cards', type: 'toggle' },
      { key: 'offlineEnabled', label: 'Work offline (cache & sync)', type: 'toggle' },
    ],
  },
  {
    id: 'desk', title: 'Desk & badges', icon: Monitor,
    description: 'Event desk access and badge printing options.',
    fields: [
      { key: 'deskPublic', label: 'Open the event desk to everyone (kiosk mode, no login)', type: 'toggle' },
      { custom: 'deskLink', label: 'Desk link' },
      { key: 'showPrintProgress', label: 'Show "Badge printing in progress" before returning to the desk', type: 'toggle', on: true },
      { key: 'badgeShareIos', label: 'Show "Share badge" on iPad / iPhone', type: 'toggle' },
      { key: 'badgeShareAndroid', label: 'Show "Share badge" on Android', type: 'toggle' },
      { key: 'offlineBadgeImport', label: 'Allow importing attendees into the Offline Badge Printer', help: 'No sign-in needed: anyone with the badge printer page can import attendee names and answers.', type: 'toggle' },
      { custom: 'badgeLinks', label: 'Badge design' },
    ],
  },
  {
    id: 'lockers', title: 'Lockers', icon: Lock,
    description: 'Cloakroom lockers for attendees.',
    fields: [
      { key: 'lockerEnabled', label: 'Enable lockers', type: 'toggle' },
      { key: 'allowSharedLockers', label: 'Allow several attendees per locker', type: 'toggle', showIf: (d) => !!d.lockerEnabled },
      { key: 'maxUsersPerLocker', label: 'Max attendees per locker', type: 'number', min: 1, fallback: 4, showIf: (d) => !!d.lockerEnabled && !!d.allowSharedLockers },
    ],
  },
  {
    id: 'selfService', title: 'Attendee self-service', icon: UserCog,
    description: '"My registration" page: view, edit, download or cancel.',
    fields: [
      { key: 'selfServiceEnabled', label: 'Let attendees view, edit and download their registration', type: 'toggle' },
      { key: 'selfServiceEditUntil', label: 'Changes allowed until (IST)', help: 'Leave empty to keep changes open.', type: 'istDateTime', showIf: (d) => !!d.selfServiceEnabled },
      { key: 'selfServiceAllowCancel', label: 'Attendees can cancel their own registration', type: 'toggle', showIf: (d) => !!d.selfServiceEnabled },
    ],
  },
  {
    id: 'website', title: 'Website & look', icon: Globe,
    description: 'Listing on the public events page, the browser tab, and page backgrounds.',
    fields: [
      { key: 'showOnWebsite', label: 'Show on the public website', type: 'toggle' },
      { custom: 'websiteInfo', label: 'Website listing', showIf: (d) => !!d.showOnWebsite },
      { key: 'pageTitle', label: 'Browser tab title', help: 'Shown on the browser tab for this event’s registration pages. Leave empty to use the event name.' },
      { custom: 'favicon', label: 'Browser tab icon' },
      { custom: 'pageBackgrounds', label: 'Page backgrounds' },
    ],
  },
  {
    id: 'teamAccess', title: 'Team access', icon: Shield,
    description: 'Choose what editors and viewers see on the registrations page.',
    fields: [{ custom: 'roleUi', label: 'Role permissions' }],
  },
  {
    id: 'teamPortal', title: 'Team sign-in page', icon: LogIn,
    description: 'A branded sign-in page for this event’s team: login ID and password only.',
    fields: [{ custom: 'teamPortal', label: 'Team sign-in page' }],
  },
  {
    id: 'domain', title: 'Custom domain', icon: Server,
    description: 'Serve registration and team sign-in on your own domain.',
    fields: [{ custom: 'customDomain', label: 'Custom domain' }],
  },
  {
    id: 'advanced', title: 'Advanced', icon: Wrench,
    description: 'Run this event from a server at the venue (LAN mode) when internet is unreliable.',
    fields: [{ custom: 'lanSync', label: 'LAN sync key' }],
  },
];

const toLocalInput = (value) => (value ? String(value).slice(0, 16) : '');

const pickCore = (event) => {
  const core = { ...event };
  SEPARATE_KEYS.forEach((key) => delete core[key]);
  return core;
};

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const cleanFormSchema = (fields) => fields?.map((field) => {
  const cleaned = { ...field };
  if (field.type !== 'dropdown' && field.type !== 'radio') {
    delete cleaned.options;
    delete cleaned.optionsWithPricing;
    delete cleaned.optionWithBlackLists;
  }
  return cleaned;
});

const Row = ({ label, help, htmlFor, children, wide, inline }) => (
  <div className={`py-4 ${inline ? 'flex items-start justify-between gap-4' : 'space-y-2'} ${wide ? 'sm:col-span-2' : ''}`}>
    <div className="min-w-0">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-900">{label}</Label>
      {help && <p className="mt-0.5 text-xs text-gray-500">{help}</p>}
    </div>
    <div className={inline ? 'shrink-0 pt-0.5' : ''}>{children}</div>
  </div>
);

const GenericField = ({ field, draft, set }) => {
  const id = `setting-${field.key}`;
  const raw = draft[field.key];
  switch (field.type) {
    case 'toggle': {
      const checked = field.on ? raw !== false : !!raw;
      return (
        <Row label={field.label} help={field.help} htmlFor={id} inline wide>
          <Switch id={id} checked={checked} onCheckedChange={(value) => set(field.key, value)} aria-label={field.label} />
        </Row>
      );
    }
    case 'select': {
      const value = field.lower ? String(raw || field.options[0][0]).toLowerCase() : (raw || field.fallback || field.options[0][0]);
      return (
        <Row label={field.label} help={field.help} htmlFor={id}>
          <select id={id} value={value} onChange={(e) => set(field.key, e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
      );
    }
    case 'number':
      return (
        <Row label={field.label} help={field.help} htmlFor={id}>
          <Input id={id} type="number" min={field.min} value={raw ?? field.fallback ?? ''}
            placeholder={field.zeroClears ? 'Unlimited' : undefined}
            onChange={(e) => {
              const value = e.target.value === '' ? null : Number(e.target.value);
              set(field.key, field.zeroClears ? (value && value > 0 ? value : 0) : value);
            }} />
        </Row>
      );
    case 'textarea':
      return (
        <Row label={field.label} help={field.help} htmlFor={id} wide={field.wide}>
          <Textarea id={id} rows={3} value={raw || ''} onChange={(e) => set(field.key, e.target.value)} />
        </Row>
      );
    case 'localDateTime':
      return (
        <Row label={field.label} help={field.help} htmlFor={id}>
          <Input id={id} type="datetime-local" value={toLocalInput(raw)} onChange={(e) => set(field.key, e.target.value)} />
        </Row>
      );
    case 'istDateTime':
      return (
        <Row label={field.label} help={field.help} htmlFor={id}>
          <Input id={id} type="datetime-local" value={toIstInputValue(raw)} onChange={(e) => set(field.key, istInputToBackend(e.target.value))} />
        </Row>
      );
    default:
      return (
        <Row label={field.label} help={field.help} htmlFor={id}>
          <Input id={id} value={raw || ''} required={field.required} onChange={(e) => set(field.key, e.target.value)} />
        </Row>
      );
  }
};

const ImageInput = ({ label, value, onChange, assetType }) => {
  const [uploading, setUploading] = useState(false);
  const { showError } = useToast();
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-900">{label}</Label>
      {value && <img src={value} alt="" className="max-h-24 rounded border object-contain" />}
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="max-w-full text-sm"
          disabled={uploading}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            setUploading(true);
            try {
              const sized = await resizeImage(file, IMAGE_LIMITS[assetType]);
              onChange(await ApiService.uploadFormSchemaAsset(assetType, sized));
            } catch (err) {
              showError(err.message || 'Upload failed');
            } finally {
              setUploading(false);
            }
          }} />
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        {value && <Button type="button" variant="outline" size="sm" onClick={() => onChange('')}>Remove</Button>}
      </div>
    </div>
  );
};

const copyText = async (text, showSuccess, showError) => {
  try {
    await navigator.clipboard.writeText(text);
    showSuccess('Copied');
  } catch (_) {
    showError('Copy failed — select and copy it manually');
  }
};

const RoleUiEditor = ({ value, onChange }) => {
  const configs = Object.fromEntries(ROLE_UI_ROLES.map((role) => [role, roleConfig(value, role)]));
  const update = (role, patch) => onChange({ ...(value || {}), ...Object.fromEntries(ROLE_UI_ROLES.map((r) => [r, configs[r]])), [role]: { ...configs[role], ...patch } });
  const toggle = (role, id, visible) => {
    const hidden = new Set(configs[role].hiddenActions);
    if (visible) hidden.delete(id); else hidden.add(id);
    update(role, { hiddenActions: Array.from(hidden) });
  };
  return (
    <div className="space-y-5 py-4">
      <p className="text-sm text-gray-600">Admins always see everything. Ticked items are visible to that role.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {ROLE_UI_ROLES.map((role) => (
          <div key={role} className="rounded-lg border p-3">
            <Label htmlFor={`search-${role}`} className="text-sm font-medium">{role === 'EDITOR' ? 'Editor' : 'Viewer'} search</Label>
            <select id={`search-${role}`} value={configs[role].searchMode} onChange={(e) => update(role, { searchMode: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="SUBMIT">Search on Enter / Search button</option>
              <option value="AUTO">Search while typing</option>
            </select>
            <Label htmlFor={`list-${role}`} className="mt-3 block text-sm font-medium">{role === 'EDITOR' ? 'Editor' : 'Viewer'} registrations list</Label>
            <select id={`list-${role}`} value={configs[role].listMode} onChange={(e) => update(role, { listMode: e.target.value })}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="ALL">Show all registrations</option>
              <option value="SEARCH_ONLY">Only show results of a search</option>
            </select>
          </div>
        ))}
      </div>
      {ROLE_UI_GROUPS.map((group) => (
        <div key={group.title} className="overflow-hidden rounded-lg border">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <span>{group.title}</span>
            <span className="w-14 text-center">Editor</span>
            <span className="w-14 text-center">Viewer</span>
          </div>
          <ul className="divide-y">
            {group.actions.map((action) => (
              <li key={action.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-2 text-sm">
                <span className="min-w-0">
                  {action.label}
                  {action.adminOnly && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">Admins only</span>}
                </span>
                {ROLE_UI_ROLES.map((role) => (
                  <span key={role} className="flex w-14 justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      aria-label={`${action.label} for ${role.toLowerCase()}s`}
                      disabled={action.adminOnly}
                      checked={!action.adminOnly && !configs[role].hiddenActions.includes(action.id)}
                      onChange={(e) => toggle(role, action.id, e.target.checked)}
                    />
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const TeamPortalEditor = ({ value, onChange, eventId, customDomain }) => {
  const portal = value || {};
  const { showSuccess, showError } = useToast();
  const set = (patch) => onChange({ ...portal, ...patch });
  const url = customDomain ? `https://${customDomain}/login` : `${window.location.origin}/team/${eventId}`;
  return (
    <div className="space-y-4 py-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label htmlFor="portal-enabled" className="text-sm font-medium text-gray-900">Enable the team sign-in page</Label>
          <p className="mt-0.5 text-xs text-gray-500">Members sign in with their login ID and password and land on this event. There is no forgot-password link: set passwords from the Team tab.</p>
        </div>
        <Switch id="portal-enabled" checked={!!portal.enabled} onCheckedChange={(enabled) => set({ enabled })} />
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50 p-2">
        <code className="min-w-0 flex-1 break-all text-xs">{url}</code>
        <Button type="button" size="sm" variant="outline" onClick={() => copyText(url, showSuccess, showError)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
        <Button type="button" size="sm" variant="outline" asChild>
          <a href={`/team/${eventId}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Preview</a>
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="portal-title">Title</Label>
          <Input id="portal-title" maxLength={120} value={portal.title || ''} placeholder="Defaults to the event name" onChange={(e) => set({ title: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="portal-color">Brand colour</Label>
          <div className="flex gap-2">
            <input id="portal-color" type="color" className="h-10 w-12 cursor-pointer rounded border" value={portal.primaryColor || '#2563eb'}
              onChange={(e) => set({ primaryColor: e.target.value })} />
            <Input value={portal.primaryColor || ''} placeholder="#2563eb" onChange={(e) => set({ primaryColor: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="portal-subtitle">Subtitle</Label>
          <Input id="portal-subtitle" maxLength={300} value={portal.subtitle || ''} placeholder="e.g. Registration desk & volunteers" onChange={(e) => set({ subtitle: e.target.value })} />
        </div>
        <ImageInput label="Logo" assetType="portal_logo" value={portal.logoUrl} onChange={(logoUrl) => set({ logoUrl })} />
        <ImageInput label="Banner (wide image, about 1600 × 900)" assetType="portal_banner" value={portal.bannerUrl} onChange={(bannerUrl) => set({ bannerUrl })} />
        <div className="space-y-2">
          <Label htmlFor="portal-fit">Banner display</Label>
          <select id="portal-fit" value={portal.bannerFit || 'COVER'} onChange={(e) => set({ bannerFit: e.target.value })}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="COVER">Fill the panel (crops edges) — photos</option>
            <option value="CONTAIN">Show the whole image — posters, text, logos</option>
          </select>
          <p className="text-xs text-gray-500">Images are resized automatically before upload.</p>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="portal-help">Help text under the form</Label>
          <Input id="portal-help" maxLength={500} value={portal.helpText || ''} placeholder="Forgot your password? Ask the event admin to set a new one for you."
            onChange={(e) => set({ helpText: e.target.value })} />
        </div>
      </div>
    </div>
  );
};

const DEFAULT_DOMAIN_TARGET = 'custom.nextwavesaas.tech';

const DnsRecordRow = ({ label, value }) => {
  const { showSuccess, showError } = useToast();
  return (
    <div className="flex items-center gap-2">
      <span className="w-14 shrink-0 text-xs text-gray-500">{label}</span>
      <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1.5 text-xs ring-1 ring-gray-200">{value}</code>
      <Button type="button" size="sm" variant="ghost" onClick={() => copyText(value, showSuccess, showError)} aria-label={`Copy ${label}`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

const CustomDomainEditor = ({ value, onChange, saved, eventId }) => {
  const host = (value || '').trim().toLowerCase();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [activating, setActivating] = useState(false);
  const { showError, showSuccess } = useToast();
  // Toast helpers change every render; keep the polling callback stable.
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const dns = status?.dns;
  const https = status?.https;
  const target = dns?.target || DEFAULT_DOMAIN_TARGET;
  const sub = host.split('.').length > 2 ? host.split('.')[0] : '@';
  const issuing = https?.status === 'ISSUING';
  const active = https?.status === 'ACTIVE';

  const check = useCallback(async () => {
    setChecking(true);
    try {
      setStatus(await ApiService.getCustomDomainStatus(eventId));
    } catch (err) {
      showErrorRef.current(err.message || 'Could not check the domain');
    } finally {
      setChecking(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (saved) check();
  }, [saved, check]);

  // Certificate issuance runs in the background; poll until it finishes.
  useEffect(() => {
    if (!issuing) return undefined;
    const timer = setInterval(check, 4000);
    return () => clearInterval(timer);
  }, [issuing, check]);

  const activate = async () => {
    setActivating(true);
    try {
      const https = await ApiService.activateCustomDomain(eventId);
      setStatus((prev) => ({ ...(prev || {}), https }));
      showSuccess('Getting the SSL certificate… this takes up to a minute.');
    } catch (err) {
      showError(err.message || 'Could not start HTTPS set-up');
    } finally {
      setActivating(false);
    }
  };

  const badge = (ok, label, tone) => (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${ok ? 'bg-green-100 text-green-800' : tone === 'red' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
      {ok ? '✓' : '•'} {label}
    </span>
  );

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="custom-domain">Domain</Label>
        <Input id="custom-domain" value={value || ''} placeholder="register.yourevent.in" autoCapitalize="none"
          onChange={(e) => onChange(e.target.value.replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim().toLowerCase())} />
        <p className="text-xs text-gray-500">Only the host name: no https:// and no path. Leave empty to remove (its certificate is removed too).</p>
      </div>

      {host && (
        <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
          <h4 className="text-sm font-semibold text-gray-900">Step 1 · Add this record at your domain’s DNS provider</h4>
          <div className="space-y-2">
            <DnsRecordRow label="Type" value="CNAME" />
            <DnsRecordRow label="Name" value={sub} />
            <DnsRecordRow label="Value" value={target} />
          </div>
          <p className="text-xs text-gray-500">
            Use a sub-domain such as register.yourevent.in (a bare domain can’t use CNAME). If your DNS is on
            Cloudflare, keep the record “DNS only” (grey cloud).
          </p>
        </div>
      )}

      {saved && (
        <div className="space-y-3 rounded-lg border p-4">
          <h4 className="text-sm font-semibold text-gray-900">Step 2 · Connect and turn on HTTPS</h4>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {dns ? badge(dns.dnsOk, dns.dnsOk ? 'DNS connected' : 'DNS not connected') : <span className="text-sm text-gray-600">Saved: <b>{saved}</b></span>}
              {https && (active ? badge(true, 'HTTPS active')
                : issuing ? badge(false, 'Getting certificate…')
                  : https.status === 'FAILED' ? badge(false, 'HTTPS failed', 'red') : badge(false, 'HTTPS not set up'))}
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={check} disabled={checking}>
                {checking && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Check status
              </Button>
              {https?.enabled && !active && (
                <Button type="button" size="sm" onClick={activate} disabled={!dns?.dnsOk || issuing || activating}
                  title={dns?.dnsOk ? '' : 'Add the DNS record first'}>
                  {(issuing || activating) && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  {https.status === 'FAILED' ? 'Retry HTTPS' : 'Activate HTTPS'}
                </Button>
              )}
            </div>
          </div>
          {dns?.message && !active && <p className="text-sm text-gray-700">{dns.message}</p>}
          {https?.error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{https.error}</p>}
          {https && !https.enabled && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              HTTPS set-up isn’t enabled on the server yet. Ask the NextWave team to run the custom-domain setup.
            </p>
          )}
          {active && (
            <p className="text-sm text-green-800">
              Live over HTTPS{https.certExpiry ? ` · certificate renews automatically (current one valid until ${https.certExpiry.replace('notAfter=', '')})` : ''}.
            </p>
          )}
          {dns && (dns.cnames?.length > 0 || dns.addresses?.length > 0) && !dns.dnsOk && (
            <p className="text-xs text-gray-500">Currently points to: {[...(dns.cnames || []), ...(dns.addresses || [])].join(', ')}</p>
          )}
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <span>Attendees register at <a className="break-all font-medium text-blue-600 hover:underline" href={`https://${saved}/`} target="_blank" rel="noopener noreferrer">https://{saved}/</a></span>
            <span>Team signs in at <a className="break-all font-medium text-blue-600 hover:underline" href={`https://${saved}/login`} target="_blank" rel="noopener noreferrer">https://{saved}/login</a></span>
          </div>
        </div>
      )}
    </div>
  );
};

const EventSettingsPage = () => {
  const { eventId, eventRef } = useEventRoute();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  const activeId = SECTIONS.some((s) => s.id === location.hash.slice(1)) ? location.hash.slice(1) : 'general';

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['eventSettings', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
    refetchOnWindowFocus: false,
  });
  const { data: schemasResponse } = useQuery({ queryKey: ['formSchemas'], queryFn: () => ApiService.getFormSchemas() });
  const formSchemas = Array.isArray(schemasResponse?.content) ? schemasResponse.content : [];

  useEffect(() => {
    if (event && !original) {
      setOriginal(event);
      setDraft(event);
    }
  }, [event, original]);

  const dirty = useMemo(() => {
    if (!draft || !original) return { any: false };
    const core = !same(pickCore(draft), pickCore(original));
    const flags = Object.fromEntries(SEPARATE_KEYS.map((key) => [key, !same(draft[key] || null, original[key] || null)]));
    return { core, ...flags, any: core || Object.values(flags).some(Boolean) };
  }, [draft, original]);

  useEffect(() => {
    if (!dirty.any) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty.any]);

  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!draft.name?.trim()) {
      showError('Event name is required');
      return;
    }
    setSaving(true);
    let next = { ...original };
    try {
      if (dirty.core) {
        const saved = await ApiService.updateEvent(eventId, { ...pickCore(draft), formSchema: cleanFormSchema(draft.formSchema) });
        next = { ...next, ...pickCore(draft), ...(saved || {}) };
      }
      if (dirty.teamPortal) {
        next.teamPortal = await ApiService.updateTeamPortal(eventId, draft.teamPortal || {});
      }
      if (dirty.roleUi) {
        next.roleUi = await ApiService.updateRoleUi(eventId, draft.roleUi || {});
      }
      if (dirty.slug) {
        const result = await ApiService.updateEventSlug(eventId, draft.slug || '');
        next.slug = result?.slug;
        rememberEvents({ id: eventId, slug: next.slug });
        navigate({ pathname: `/admin/events/${next.slug}/settings`, hash: location.hash }, { replace: true });
      }
      if (dirty.customDomain) {
        const result = await ApiService.updateCustomDomain(eventId, draft.customDomain || '');
        next.customDomain = result?.customDomain || null;
      }
      SEPARATE_KEYS.forEach((key) => { if (!dirty[key]) next[key] = original[key]; });
      setOriginal(next);
      setDraft((prev) => ({ ...prev, ...next }));
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      showSuccess('Settings saved');
    } catch (err) {
      // Whatever saved before the failure is kept so a retry only sends the rest.
      setOriginal({ ...next });
      showError(err.message || 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || (!draft && !error)) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
  }
  if (error || !draft) {
    return <div className="p-6 text-center text-gray-600">Could not load this event. {error?.message}</div>;
  }

  const q = query.trim().toLowerCase();
  const matches = (field, section) => !q || `${field.label} ${field.help || ''} ${section.title}`.toLowerCase().includes(q);
  const visibleSections = q
    ? SECTIONS.map((s) => ({ ...s, fields: s.fields.filter((f) => matches(f, s)) })).filter((s) => s.fields.length)
    : SECTIONS.filter((s) => s.id === activeId);

  const renderCustom = (name) => {
    switch (name) {
      case 'slug':
        return (
          <Row label="Link name" help={`Used in every link: ${window.location.origin}/register/${draft.slug || 'your-event'}. Changing it breaks links already shared.`}>
            <Input value={draft.slug || ''} placeholder="up-pathcon-2026"
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-'))} />
          </Row>
        );
      case 'price':
        return (
          <Row label="Price" help="Base price; per-option prices are set in the form fields.">
            <div className="flex gap-2">
              <select aria-label="Currency" value={draft.currency || 'INR'} onChange={(e) => set('currency', e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-2 text-sm">
                {['INR', 'USD', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input type="number" step="0.01" min="0" value={draft.price ?? ''} onChange={(e) => set('price', parseFloat(e.target.value) || 0)} />
            </div>
          </Row>
        );
      case 'poweredBy':
        return (
          <Row label="Powered by logos" help="Shown next to the EventEase logo. PNG or WebP, about 200 × 80 px." wide>
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                {(draft.poweredByLogos || []).map((src) => (
                  <div key={src} className="relative rounded border bg-white p-2">
                    <img src={src} alt="" className="h-10 w-auto object-contain" />
                    <button type="button" className="absolute -right-2 -top-2 rounded-full bg-white p-0.5 shadow" aria-label="Remove logo"
                      onClick={() => set('poweredByLogos', (draft.poweredByLogos || []).filter((item) => item !== src))}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm">
                Add logo
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  try {
                    const url = await ApiService.uploadFormSchemaAsset('ticketing_icon', await resizeImage(file, IMAGE_LIMITS.ticketing_icon || {}));
                    set('poweredByLogos', [...(draft.poweredByLogos || []), url]);
                  } catch (err) {
                    showError(err.message || 'Upload failed');
                  }
                }} />
              </label>
            </div>
          </Row>
        );
      case 'onSpotForm':
        return (
          <Row label="On-spot form" help="Use a different (e.g. shorter) form for on-spot registrations." wide>
            <select value={draft.onSpotSchemaId || ''} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:w-96"
              onChange={(e) => {
                const schema = formSchemas.find((s) => s.schemaId === e.target.value);
                setDraft((prev) => ({
                  ...prev,
                  onSpotSchemaId: schema ? schema.schemaId : '',
                  onSpotFormSchema: schema ? JSON.parse(JSON.stringify(schema.fields || [])) : [],
                }));
              }}>
              <option value="">Same as registration form</option>
              {formSchemas.map((schema) => <option key={schema.schemaId} value={schema.schemaId}>{schema.name}</option>)}
            </select>
          </Row>
        );
      case 'paymentLinks':
        return (
          <Row label="Gateway, merchants and coupons" help="Managed on their own pages." wide>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild><Link to={`/admin/events/${eventRef}/payment-settings`}>Payment settings</Link></Button>
              <Button variant="outline" size="sm" asChild><Link to="/admin/settings/coupons">Coupons</Link></Button>
            </div>
          </Row>
        );
      case 'invoice': {
        const settings = draft.invoiceSettings || {};
        const setInvoice = (patch) => set('invoiceSettings', { ...settings, ...patch });
        const fields = [
          ['sellerName', 'Legal name (supplier)'], ['gstin', 'GSTIN'], ['pan', 'PAN'], ['address', 'Address'],
          ['state', 'State'], ['stateCode', 'State code'], ['placeOfSupply', 'Place of supply (blank = supplier state)'],
          ['sacCode', 'SAC code'], ['invoicePrefix', 'Invoice number prefix'], ['email', 'Billing email'],
          ['phone', 'Billing phone'], ['buyerGstinField', 'Form field with attendee GSTIN'], ['authorisedSignatory', 'Authorised signatory'],
        ];
        return (
          <div className="sm:col-span-2">
            <Row label="Send a GST tax invoice" help="Amounts are treated as GST-inclusive. Without a GSTIN only the receipt is sent." inline wide>
              <Switch checked={!!settings.enabled} onCheckedChange={(enabled) => setInvoice({ enabled, gstRate: settings.gstRate ?? 18 })} aria-label="Send a GST tax invoice" />
            </Row>
            {settings.enabled && (
              <div className="grid gap-3 pb-4 sm:grid-cols-2">
                {fields.map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`invoice-${key}`} className="text-xs text-gray-600">{label}</Label>
                    <Input id={`invoice-${key}`} value={settings[key] || ''} onChange={(e) => setInvoice({ [key]: e.target.value })} />
                  </div>
                ))}
                <div className="space-y-1">
                  <Label htmlFor="invoice-gstRate" className="text-xs text-gray-600">GST rate % (included in price)</Label>
                  <Input id="invoice-gstRate" type="number" min={0} step="0.01" value={settings.gstRate ?? 18}
                    onChange={(e) => { const v = parseFloat(e.target.value); setInvoice({ gstRate: Number.isFinite(v) && v >= 0 ? v : 0 }); }} />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="invoice-footerNote" className="text-xs text-gray-600">Footer note / terms</Label>
                  <Textarea id="invoice-footerNote" rows={2} value={settings.footerNote || ''} onChange={(e) => setInvoice({ footerNote: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        );
      }
      case 'scanLogo': {
        const fallback = draft.formLogoUrl || draft.eventLogo;
        return (
          <div className="py-4 sm:col-span-2">
            <ImageInput label="Scan page logo (empty = registration form logo)" assetType="scan_logo" value={draft.scanLogoUrl}
              onChange={(url) => set('scanLogoUrl', url)} />
            {!draft.scanLogoUrl && <p className="mt-1 text-xs text-gray-500">{fallback ? 'Using the form logo.' : 'No form logo set yet.'}</p>}
          </div>
        );
      }
      case 'publicSearchFields': {
        const labels = (draft.formSchema || []).map((f) => f.label).filter(Boolean);
        if (!labels.length) return null;
        const normalize = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const selected = (draft.publicSearchFields ?? DEFAULT_PUBLIC_SEARCH_FIELDS).map(normalize);
        const toggle = (label, checked) => {
          const current = labels.filter((l) => selected.includes(normalize(l)));
          set('publicSearchFields', checked ? [...current, label] : current.filter((l) => l !== label));
        };
        return (
          <Row label="Public search fields" help="Fields shown to scanners who are not signed in (none = no public search)." wide>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {labels.map((label) => (
                <label key={label} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4" checked={selected.includes(normalize(label))} onChange={(e) => toggle(label, e.target.checked)} />
                  <span className="min-w-0 break-words">{label}</span>
                </label>
              ))}
            </div>
          </Row>
        );
      }
      case 'deskLink': {
        const url = `${window.location.origin}/desk/${eventRef}`;
        return (
          <Row label="Desk link" help={'Without login the desk shows on-spot registration only when access is "Anyone".'} wide>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-gray-100 px-2 py-1.5 text-xs">{url}</code>
              <Button type="button" variant="outline" size="sm" onClick={() => copyText(url, showSuccess, showError)}>Copy</Button>
            </div>
          </Row>
        );
      }
      case 'badgeLinks':
        return (
          <Row label="Badge design" wide>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild><Link to={`/admin/events/${eventRef}/badge-config`}><BadgeCheck className="mr-1 h-4 w-4" />Dynamic badge</Link></Button>
              <Button variant="outline" size="sm" asChild><Link to={`/admin/events/${eventRef}/badges`}>Badge config</Link></Button>
            </div>
          </Row>
        );
      case 'favicon':
        return (
          <ImageInput label="Browser tab icon" value={draft.faviconUrl} assetType="ticketing_icon"
            onChange={(url) => set('faviconUrl', url || '')} />
        );
      case 'websiteInfo': {
        const info = draft.websiteInfo || {};
        const update = (patch) => set('websiteInfo', { ...info, ...patch });
        const links = info.links || [];
        return (
          <div className="space-y-3 border-l-2 border-blue-100 py-2 pl-4 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={!!info.allowRegistration} onChange={(e) => update({ allowRegistration: e.target.checked })} />
              Allow registration from the public events page
            </label>
            <div className="space-y-1">
              <Label htmlFor="website-about" className="text-sm">About the event</Label>
              <Textarea id="website-about" rows={3} maxLength={3000} value={info.about || ''} onChange={(e) => update({ about: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="website-official" className="text-sm">Official website</Label>
                <Input id="website-official" placeholder="https://" value={info.officialUrl || ''} onChange={(e) => update({ officialUrl: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="website-organiser" className="text-sm">Organising committee link</Label>
                <Input id="website-organiser" placeholder="https://" value={info.organiserUrl || ''} onChange={(e) => update({ organiserUrl: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">More links (brochure, venue map, …)</Label>
              {links.map((link, index) => (
                <div key={index} className="flex flex-col gap-2 sm:flex-row">
                  <Input className="sm:w-40" placeholder="Label" value={link.label || ''}
                    onChange={(e) => update({ links: links.map((l, i) => (i === index ? { ...l, label: e.target.value } : l)) })} />
                  <Input placeholder="https://" value={link.url || ''}
                    onChange={(e) => update({ links: links.map((l, i) => (i === index ? { ...l, url: e.target.value } : l)) })} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => update({ links: links.filter((_, i) => i !== index) })}>Remove</Button>
                </div>
              ))}
              {links.length < 10 && (
                <Button type="button" variant="outline" size="sm" onClick={() => update({ links: [...links, { label: '', url: '' }] })}>Add link</Button>
              )}
            </div>
          </div>
        );
      }
      case 'pageBackgrounds':
        return <div className="py-4 sm:col-span-2"><PageBackgroundsEditor value={draft.pageBackgrounds} onChange={(next) => set('pageBackgrounds', next)} /></div>;
      case 'roleUi':
        return <div className="sm:col-span-2"><RoleUiEditor value={draft.roleUi} onChange={(next) => set('roleUi', next)} /></div>;
      case 'teamPortal':
        return (
          <div className="sm:col-span-2">
            <TeamPortalEditor value={draft.teamPortal} onChange={(next) => set('teamPortal', next)} eventId={original.slug || eventId} customDomain={original.customDomain} />
          </div>
        );
      case 'customDomain':
        return (
          <div className="sm:col-span-2">
            <CustomDomainEditor value={draft.customDomain} onChange={(next) => set('customDomain', next)} saved={original.customDomain} eventId={eventId} />
          </div>
        );
      case 'lanSync':
        return <div className="py-4 sm:col-span-2"><LanSyncKeyPanel eventId={eventId} /></div>;
      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-6xl pb-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <Link to={`/admin/events/${eventRef}/registrations`} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
            <ArrowLeft className="mr-1 h-4 w-4" /> Registrations
          </Link>
          <h1 className="mt-1 truncate text-2xl font-bold text-gray-900">{original.name}</h1>
          <p className="text-sm text-gray-500">Event settings</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a setting" className="pl-9 pr-8" aria-label="Find a setting" />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400" aria-label="Clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[230px_1fr] lg:gap-6">
        <nav aria-label="Settings sections" className="sticky top-0 z-20 -mx-4 mb-4 overflow-x-auto border-b bg-gray-50/95 px-4 py-2 backdrop-blur lg:static lg:mx-0 lg:mb-0 lg:overflow-visible lg:border-0 lg:bg-transparent lg:p-0">
          <ul className="flex gap-1 lg:sticky lg:top-4 lg:flex-col">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              const active = !q && section.id === activeId;
              return (
                <li key={section.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => { setQuery(''); navigate({ hash: section.id }, { replace: true }); }}
                    className={`flex w-full items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${active ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {section.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="space-y-6">
          {visibleSections.length === 0 && <p className="rounded-lg border bg-white p-6 text-sm text-gray-500">No settings match “{query}”.</p>}
          {visibleSections.map((section) => (
            <section key={section.id} className="rounded-xl border bg-white shadow-sm">
              <header className="border-b px-4 py-4 sm:px-6">
                <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                <p className="mt-0.5 text-sm text-gray-500">{section.description}</p>
              </header>
              <div className="grid divide-y px-4 sm:grid-cols-2 sm:gap-x-6 sm:divide-y-0 sm:px-6 [&>*]:border-gray-100 sm:[&>*]:border-b">
                {section.fields
                  .filter((field) => !field.showIf || field.showIf(draft))
                  .map((field) => (
                    <React.Fragment key={field.key || field.custom}>
                      {field.custom ? renderCustom(field.custom) : <GenericField field={field} draft={draft} set={set} />}
                    </React.Fragment>
                  ))}
              </div>
            </section>
          ))}
          <p className="flex items-center gap-1 text-xs text-gray-500">
            <KeyRound className="h-3.5 w-3.5" /> Registration form fields are edited from Events → Edit.
          </p>
        </div>
      </div>

      {dirty.any && (
      <div className="sticky bottom-0 z-30 mt-6 rounded-xl border bg-white/95 shadow-lg backdrop-blur">
        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-gray-700">You have unsaved changes</span>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => setDraft(original)} disabled={saving}>Discard</Button>
            <Button className="flex-1 sm:flex-none" onClick={save} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes
            </Button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default EventSettingsPage;
